/** Fog per player: what is lit now, and the stale snapshots of what was.
 *
 * Two operations, and the difference between them is the whole design. A hero
 * step *adds* light (`revealStep`); a turn boundary *recomputes* it
 * (`recomputeVisibility`), demoting last turn's lit cells to remembered first.
 * Explored ground is never un-explored, only un-watched.
 */

import type { HeroId, ObjectId, PlayerId, TownId } from "../ids.js";
import type { FogState, GameState, HeroState, MapObjectState, TownState } from "../state.js";
import { demoteVisible, isVisible, revealAround, type FogSnapshot } from "../map/fog.js";
import { indexOf } from "../map/create.js";
import { entityKeys } from "../util/iter.js";
import type { Ctx } from "./ctx.js";
import { absoluteDay, putFog } from "./ctx.js";
import { disguisedLevel, scoutingRadiusOf } from "./hero.js";
import { hiddenFrom } from "./stealth.js";

/** A town watches its surroundings whether or not a hero stands in it. */
export const TOWN_SIGHT_RADIUS = 5;
/** A flagged mine or dwelling keeps a small watch, as in the classic. */
export const OWNED_OBJECT_SIGHT_RADIUS = 2;

export const memoryKeyHero = (id: HeroId): string => `hero:${id}`;
export const memoryKeyTown = (id: TownId): string => `town:${id}`;
export const memoryKeyObject = (id: ObjectId): string => `object:${id}`;

function heroSnapshot(s: GameState, h: HeroState): FogSnapshot {
  return {
    kind: "hero", id: h.id, def: h.def, owner: h.owner,
    // What the watcher remembers is what the watcher was shown, so a disguised
    // hero is remembered weak long after they have walked out of sight.
    x: h.x, y: h.y, z: h.z, level: disguisedLevel(h), day: absoluteDay(s),
  };
}

function townSnapshot(s: GameState, t: TownState): FogSnapshot {
  return {
    kind: "town", id: t.id, faction: t.faction, owner: t.owner, name: t.name,
    x: t.x, y: t.y, z: t.z, built: t.built.length, day: absoluteDay(s),
  };
}

function objectSnapshot(s: GameState, o: MapObjectState): FogSnapshot {
  return {
    kind: "object", id: o.id, def: o.def, objectKind: o.kind, owner: o.owner,
    x: o.x, y: o.y, z: o.z, day: absoluteDay(s),
  };
}

/** Light everything one hero sees from where it now stands. */
export function revealStep(c: Ctx, h: HeroState): readonly number[] {
  const fog = c.s.fog[h.owner];
  if (fog === undefined) return [];
  const cells = revealAround(fog, h.x, h.y, h.z, scoutingRadiusOf(c, h), c.s.map);
  if (cells.length > 0) {
    c.events.push({ type: "FogRevealed", player: h.owner, cells });
  }
  return cells;
}

/**
 * Recompute one player's whole visibility plane and refresh their memory.
 *
 * Called at the start of that player's turn, not after every step: demoting
 * mid-turn would make a hero forget the ground it walked past two tiles ago.
 */
export function recomputeVisibility(c: Ctx, p: PlayerId): void {
  const fog = c.s.fog[p];
  if (fog === undefined) return;
  demoteVisible(fog);

  const revealed: number[] = [];
  for (const id of entityKeys(c.s.heroes)) {
    const h = c.s.heroes[id]!;
    if (h.owner !== p) continue;
    for (const i of revealAround(fog, h.x, h.y, h.z, scoutingRadiusOf(c, h), c.s.map)) {
      revealed.push(i);
    }
  }
  for (const id of entityKeys(c.s.towns)) {
    const t = c.s.towns[id]!;
    if (t.owner !== p) continue;
    for (const i of revealAround(fog, t.x, t.y, t.z, TOWN_SIGHT_RADIUS, c.s.map)) {
      revealed.push(i);
    }
  }
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id]!;
    if (o.owner !== p || o.removed) continue;
    for (const i of revealAround(fog, o.x, o.y, o.z, OWNED_OBJECT_SIGHT_RADIUS, c.s.map)) {
      revealed.push(i);
    }
  }
  if (revealed.length > 0) {
    revealed.sort((a, b) => a - b);
    c.events.push({ type: "FogRevealed", player: p, cells: revealed });
  }
  refreshMemory(c, p);
}

/**
 * Write back what this player can currently see of everyone else.
 *
 * The whole memory record is rebuilt in one pass with sorted keys rather than
 * calling `rememberEntity` per entity: the record is replaced either way, and a
 * single sorted rebuild is the shape that hashes identically no matter what
 * order the entities were seen in (I3).
 */
export function refreshMemory(c: Ctx, p: PlayerId): void {
  const fog = c.s.fog[p];
  if (fog === undefined) return;
  const next: Record<string, FogSnapshot> = {};
  const keep: string[] = [];
  const fresh = new Map<string, FogSnapshot>();

  const seeAt = (x: number, y: number, z: number): boolean =>
    isVisible(fog, indexOf(c.s.map, x, y, z));

  for (const id of entityKeys(c.s.heroes)) {
    const h = c.s.heroes[id]!;
    if (h.owner === p) continue;
    // A hero the observation refuses to publish must not be pinned in the fog
    // memory either. Remembering one would make stealth worse than useless: the
    // hero would vanish from the live list and stay on the minimap for ever,
    // which is the shape of a bug rather than of a hiding place.
    if (hiddenFrom(c, c.content, h, p)) continue;
    if (seeAt(h.x, h.y, h.z)) fresh.set(memoryKeyHero(h.id), heroSnapshot(c.s, h));
  }
  for (const id of entityKeys(c.s.towns)) {
    const t = c.s.towns[id]!;
    if (t.owner === p) continue;
    if (seeAt(t.x, t.y, t.z)) fresh.set(memoryKeyTown(t.id), townSnapshot(c.s, t));
  }
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id]!;
    if (o.removed || o.owner === p) continue;
    if (seeAt(o.x, o.y, o.z)) fresh.set(memoryKeyObject(o.id), objectSnapshot(c.s, o));
  }

  // A hero the player is watching walk away is remembered where it was last
  // seen; one that was never seen has no entry at all.
  for (const k of entityKeys(fog.memory)) if (!fresh.has(k)) keep.push(k);
  const keys = [...keep, ...[...fresh.keys()]].sort();
  for (const k of keys) next[k] = fresh.get(k) ?? fog.memory[k]!;

  putFog(c, p, { seen: fog.seen, memory: next } satisfies FogState);
}

/** Drop a memory entry, for an entity that has provably ceased to exist. */
export function forgetRemembered(c: Ctx, p: PlayerId, key: string): void {
  const fog = c.s.fog[p];
  if (fog === undefined || !(key in fog.memory)) return;
  const next: Record<string, FogSnapshot> = {};
  for (const k of entityKeys(fog.memory)) if (k !== key) next[k] = fog.memory[k]!;
  putFog(c, p, { seen: fog.seen, memory: next });
}
